import { FunctionComponent, useCallback, useContext, useEffect, useMemo } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useNavigate } from 'react-router-dom';
import { DetailsModalContext } from '../../context';
import {
  Comments,
  FeedCardLoader,
  FeedCardMargin,
  getStoryCardName,
  layoutPath,
  useAppSelector,
  useCommentsFeed,
} from '../../shared';
import { NoContent } from '../Error';

import classes from './CommentsFeed.module.scss';

interface CardDetailsArgs {
  storyId: number;
  storyCardId: number;
  scrollToComments?: boolean;
}

export const CommentsFeed: FunctionComponent = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const { getCommentsFeedItems, loadMoreCommentsFeedItems } = useCommentsFeed();

  const { setDetailsModalData } = useContext(DetailsModalContext);

  const {
    commentsFeed: comments,
    hasNextPage,
    isFetched,
  } = useAppSelector(({ commentsFeed }) => commentsFeed);

  const hasComments = useMemo(() => Boolean(comments.length), [comments.length]);

  useEffect(() => {
    getCommentsFeedItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cardDetails = useCallback(
    ({ storyId, storyCardId, scrollToComments }: CardDetailsArgs) => {
      if (!isMobileOnly) {
        setDetailsModalData({ storyId, cardId: storyCardId, scrollToComments });
        return;
      }

      navigate(
        layoutPath(
          `/details/${storyId}/${storyCardId}${scrollToComments ? '?scrollToComments=true' : ''}`
        )
      );
    },
    [navigate, setDetailsModalData]
  );

  const content = useMemo(() => {
    if (!hasComments) {
      return <NoContent />;
    }

    return (
      <InfiniteScroll
        next={loadMoreCommentsFeedItems}
        hasMore={hasNextPage}
        loader={<FeedCardLoader />}
        dataLength={comments.length}
        className={classes['comments-feed__content']}
      >
        <div className={classes['comments-feed__content-cards']}>
          {comments.map((comment) => {
            const { id, storyId, storyCardId, storyCard } = comment;

            return (
              <div key={id} className={classes['comments-feed__content-cards-item']}>
                <FeedCardMargin top right left>
                  <button
                    type={'button'}
                    className={classes['comments-feed__content-cards-item-link']}
                    onClick={() => cardDetails({ storyId, storyCardId })}
                  >
                    {getStoryCardName({ card: storyCard, t })}
                  </button>
                  <Comments
                    storyId={storyId}
                    cardId={storyCardId}
                    comments={[comment]}
                    commentsRootCount={1}
                    loadMore={() => cardDetails({ storyId, storyCardId, scrollToComments: true })}
                  />
                </FeedCardMargin>
              </div>
            );
          })}
        </div>
      </InfiniteScroll>
    );
  }, [cardDetails, comments, hasComments, hasNextPage, loadMoreCommentsFeedItems, t]);

  if (!isFetched) {
    return <FeedCardLoader />;
  }

  return (
    <div className={classes['comments-feed']}>
      <h1 className={classes['comments-feed__title']}>{t('adminLayoutMenu.comments')}</h1>
      {content}
    </div>
  );
};
